import type {
  IExecutionMetrics,
  IVerificationMetrics,
  TestAccuracySection,
} from '@/feature/testAccuracy/types/testAccuracy';
import {
  formatRate,
  getVerificationPassRate,
  rateTone,
  rateToneClass,
  toPct,
} from '@/feature/testAccuracy/utils/testAccuracyFormat';
import Panel from '@/shared/components/ui/Panel';
import StatCard from '@/shared/components/ui/StatCard';

interface ITestAccuracySummaryProps {
  section: TestAccuracySection;

  verification?: IVerificationMetrics | null;

  execution?: IExecutionMetrics | null;
}

const TestAccuracySummary = ({ section, verification, execution }: ITestAccuracySummaryProps) => {
  if (section === 'verification') {
    const vs = verification || {};

    const vTotal = vs.total_executed ?? 0;

    const verificationPassRate = getVerificationPassRate(vs);

    const passRateTone = rateTone(verificationPassRate);

    return (
      <Panel title='Verification' titleAddon=' — pre-verify runs · per test case'>
        <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3'>
          <StatCard value={vTotal} label='Total Test Cases' />

          <StatCard
            value={formatRate(toPct(vs.passed_1st_attempt ?? 0, vTotal))}
            subLabel={String(vs.passed_1st_attempt ?? 0)}
            label='Passed 1st Attempt'
            valueClassName='text-emerald-700'
          />

          <StatCard
            value={formatRate(toPct(vs.passed_2nd_attempt ?? 0, vTotal))}
            subLabel={String(vs.passed_2nd_attempt ?? 0)}
            label='Passed 2nd Attempt'
            valueClassName='text-emerald-700'
          />

          <StatCard
            value={formatRate(toPct(vs.passed_3rd_plus_attempt ?? 0, vTotal))}
            subLabel={String(vs.passed_3rd_plus_attempt ?? 0)}
            label='Passed 3rd+ Attempt'
            valueClassName='text-yellow-600'
          />

          <StatCard
            value={formatRate(toPct(vs.not_passed ?? 0, vTotal))}
            subLabel={String(vs.not_passed ?? 0)}
            label='Never Passed'
            valueClassName='text-red-700'
          />

          <StatCard
            value={formatRate(verificationPassRate)}
            subLabel={String((vs.passed_1st_attempt ?? 0) + (vs.passed_2nd_attempt ?? 0))}
            label='Verification Efficiency'
            valueClassName={rateToneClass(passRateTone)}
          />
        </div>
      </Panel>
    );
  }

  const es = execution || {};

  const eTotal = es.total_executed ?? 0;

  const executionEfficientCount =
    (es.passed_after_first_verify ?? 0) + (es.required_reverification ?? 0);

  const executionEfficiencyRate = toPct(executionEfficientCount, eTotal);

  const executionEfficiencyTone = rateTone(executionEfficiencyRate);

  return (
    <Panel title='Execution' titleAddon=' — script runs · after first verify · per test case'>
      <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3'>
        <StatCard value={eTotal} label='Total Test Cases' />

        <StatCard
          value={formatRate(toPct(es.passed_after_first_verify ?? 0, eTotal))}
          subLabel={String(es.passed_after_first_verify ?? 0)}
          label='Passed'
          valueClassName='text-emerald-700'
        />

        <StatCard
          value={formatRate(toPct(es.required_reverification ?? 0, eTotal))}
          subLabel={String(es.required_reverification ?? 0)}
          label='Passed After Re-verify'
          valueClassName='text-emerald-700'
        />

        <StatCard
          value={formatRate(toPct(es.never_passed_after_verify ?? 0, eTotal))}
          subLabel={String(es.never_passed_after_verify ?? 0)}
          label='Failed'
          valueClassName='text-red-700'
        />

        <StatCard
          value={formatRate(toPct(es.regressed ?? 0, eTotal))}
          subLabel={String(es.regressed ?? 0)}
          label='Regressed'
          valueClassName='text-red-700'
        />

        <StatCard
          value={formatRate(executionEfficiencyRate)}
          subLabel={String(executionEfficientCount)}
          label='Execution Efficiency'
          valueClassName={rateToneClass(executionEfficiencyTone)}
        />
      </div>
    </Panel>
  );
};

export default TestAccuracySummary;
